// What an uploaded file is, for the card that shows it and the view that opens it. Pure.
// The folder endpoint says per asset version: the file name, the asset kind (image, video, audio, doc, other) and
// the mime type. A script or a character description someone's own Claude put in a folder is a doc or other.

/** How the document view shows a file. */
export type FileShape = "text" | "pdf" | "image" | "video" | "audio" | "file";

export interface FileFacts {
  name: string | null;
  /** ff.asset.kind: image, video, audio, timeline, doc, other */
  assetKind: string | null;
  mime: string | null;
}

const TEXT_NAME = /\.(md|markdown|txt|text|fountain|spmd)$/i;
const MARKDOWN = /\.(md|markdown)$/i;
const WORD = /\.(docx?|odt|rtf|pages)$/i;
const WORD_MIME = /(msword|wordprocessingml|opendocument\.text|rtf)/i;

const lower = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();

/** The shape of a file: by its mime type first, then its name, then the asset kind. */
export function fileShape(f: FileFacts): FileShape {
  const mime = lower(f.mime);
  const name = lower(f.name);
  if (mime.startsWith("image/") || (!mime && f.assetKind === "image")) return "image";
  if (mime.startsWith("video/") || (!mime && f.assetKind === "video")) return "video";
  if (mime.startsWith("audio/") || (!mime && f.assetKind === "audio")) return "audio";
  if (mime === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (mime.startsWith("text/") || mime === "application/json" || TEXT_NAME.test(name)) return "text";
  return "file";
}

/** Pictures and clips: they have a still on a card and can become shots. */
export const isMedia = (f: FileFacts): boolean => {
  const s = fileShape(f);
  return s === "image" || s === "video";
};

/** A file we can read as text (Markdown, plain text, Fountain): it can move to the Writing step. */
export const isText = (f: FileFacts): boolean => fileShape(f) === "text";

/**
 * The words of a file name, lower case and without its extension: split on _ - . and spaces, on camelCase and
 * between letters and digits ("E01_pilotScript.md" is e, 01, pilot, script). Kinds are read from whole words, so
 * "character_description" is no script and "broadcast" no cast.
 */
export function nameWords(name: string | null | undefined): string[] {
  return (name ?? "")
    .trim()
    .replace(/\.[a-z0-9]{1,10}$/i, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Za-z])(\d)|(\d)([A-Za-z])/g, (_m, a, b, c, d) => (a ? `${a} ${b}` : `${c} ${d}`))
    .split(/[\s_.\-–—()[\]]+/)
    .map((w) => w.toLowerCase())
    .filter(Boolean);
}

const SCRIPT_EXT = /\.(fountain|spmd)$/i;
/** What the sheet import calls script pages (a .fountain file): the server's own string. */
const SCRIPT_PAGES = "Script pages";
const SCRIPT_WORDS = new Set(["script", "scripts", "scenario", "screenplay", "teleplay", "draaiboek"]);
const CHARACTER_WORDS = new Set([
  "character",
  "characters",
  "personage",
  "personages",
  "persona",
  "cast",
  "bio",
]);

/**
 * Writing's own documents by their file name, exactly as the sheet import reads them (NAMED_KINDS in
 * modules/sheets, submit.ts): the web app does not import server packages, so keep the two equal. A file name the
 * server knows beats the kind the web app guesses (sent as defaultKind), so they must agree.
 */
const NAMED_KINDS: readonly [RegExp, string][] = [
  [/\bsynops[ie]s\b/i, "Synopsis"],
  [/\btreatment\b/i, "Treatment"],
  [/\blog ?line\b/i, "Logline"],
  [/\bbeat ?sheet\b/i, "Beat sheet"],
  [/\boutline\b/i, "Outline"],
  [/\b(series )?bible\b/i, "Series bible"],
];

/** The words the server reads a file name as (titleFromFileName in modules/sheets): no folder, no extension. */
const serverTitle = (name: string) =>
  name
    .replace(/^.*[\\/]/, "")
    .replace(/\.[^.]+$/, "")
    .replace(/[_\s-]+/g, " ")
    .trim();

/** The kind the sheet import gives a file by its name alone, as the server does (kindFromFileName); else null. */
export function serverKindFromName(name: string | null | undefined): string | null {
  const n = (name ?? "").trim();
  if (!n) return null;
  if (SCRIPT_EXT.test(n)) return SCRIPT_PAGES;
  const words = serverTitle(n);
  return NAMED_KINDS.find(([re]) => re.test(words))?.[1] ?? null;
}

/** A file its name calls a script: a .fountain or .spmd file, or "script", "screenplay", … as a whole word. */
export const isScriptName = (name: string | null | undefined): boolean =>
  SCRIPT_EXT.test((name ?? "").trim()) || nameWords(name).some((w) => SCRIPT_WORDS.has(w));

// a scene heading at the start of a line: INT. EXT. EST. INT./EXT. INT/EXT I/E (Fountain allows any case)
const SCENE_HEADING = /^[ \t]*(?:(?:INT\.?\/EXT|EXT\.?\/INT|I\/E)\.?|INT\.|EXT\.|EST\.)[ \t]+\S/im;

/** Text that reads as a screenplay: a .fountain/.spmd file, or one with a scene heading at the start of a line. */
export const looksLikeScreenplay = (text: string, name?: string | null): boolean =>
  SCRIPT_EXT.test((name ?? "").trim()) || SCENE_HEADING.test(text);

/** The short kind on a card: Script, Document, PDF, Word file, Image, Video, Sound, File. */
export function kindLabel(f: FileFacts): string {
  const name = f.name ?? "";
  switch (fileShape(f)) {
    case "image":
      return "Image";
    case "video":
      return "Video";
    case "audio":
      return "Sound";
    case "pdf":
      return "PDF";
    case "text":
      return isScriptName(name) ? "Script" : MARKDOWN.test(name) ? "Document" : "Text file";
    default:
      return WORD.test(name) || WORD_MIME.test(f.mime ?? "") ? "Word file" : "File";
  }
}

/** Kinds a document can become on the Writing step: the server's names first, then the web app's own two. */
export const WRITING_KINDS = [
  SCRIPT_PAGES,
  "Synopsis",
  "Treatment",
  "Logline",
  "Outline",
  "Beat sheet",
  "Series bible",
  "Character description",
  "Notes",
] as const;

/**
 * The writing sheet kind a file most likely is: what the server reads in its name first (a .fountain file, a
 * synopsis, treatment, logline...), so the folder and the Writing desk give the same file the same kind; else a
 * script by its name or its text (`screenplay`), a character description, or notes.
 */
export function guessWritingKind(name: string | null, screenplay = false): string {
  const known = serverKindFromName(name);
  if (known) return known;
  if (screenplay || isScriptName(name)) return SCRIPT_PAGES;
  if (nameWords(name).some((w) => CHARACTER_WORDS.has(w))) return "Character description";
  return "Notes";
}

/** A size in words: 12 KB, 1.4 MB. */
export function sizeText(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);

/** The facts of an asset version from the folder endpoint's summary (name, kind, mime). */
export function factsFromSummary(summary: Record<string, unknown> | null | undefined): FileFacts {
  const s = summary ?? {};
  return { name: str(s.name), assetKind: str(s.kind), mime: str(s.mime) };
}
